import type React from 'react';
import {
  Box,
  Card,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Label,
  Paragraph,
  Select,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconPetsFilled } from '@zillow/constellation-icons';
import { Controller, useFormContext } from 'react-hook-form';
import { TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { ASSISTANCE_ANIMALS_URL } from '../../../../constants/urls';
import { get } from 'lodash-es';
import { useEffect } from 'react';
import { FIELD_IDS } from '../../../../constants/wizard';

export function PetsStep() {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();

  useEffect(() => {
    if (!getValues(FIELD_IDS.SMALL_DOGS)) {
      setValue(FIELD_IDS.SMALL_DOGS, 0);
    }
    if (!getValues(FIELD_IDS.LARGE_DOGS)) {
      setValue(FIELD_IDS.LARGE_DOGS, 0);
    }
    if (!getValues(FIELD_IDS.CATS)) {
      setValue(FIELD_IDS.CATS, 0);
    }
    if (!getValues(FIELD_IDS.PETS_OTHER)) {
      setValue(FIELD_IDS.PETS_OTHER, 0);
    }
    // Limit was changed from 99 to 9
    if (getValues(FIELD_IDS.SMALL_DOGS) > 9) {
      setValue(FIELD_IDS.SMALL_DOGS, 9);
    }
    if (getValues(FIELD_IDS.LARGE_DOGS) > 9) {
      setValue(FIELD_IDS.LARGE_DOGS, 9);
    }
    if (getValues(FIELD_IDS.CATS) > 9) {
      setValue(FIELD_IDS.CATS, 9);
    }
    if (getValues(FIELD_IDS.PETS_OTHER) > 9) {
      setValue(FIELD_IDS.PETS_OTHER, 9);
    }
    // We removed the medium dogs question, set the value to 0 incase there was an old value
    setValue(FIELD_IDS.MEDIUM_DOGS, 0);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const numSmallDogs = watch(FIELD_IDS.SMALL_DOGS);
  const numLargeDogs = watch(FIELD_IDS.LARGE_DOGS);
  const numCats = watch(FIELD_IDS.CATS);
  const numOther = watch(FIELD_IDS.PETS_OTHER);

  const hasPets = !!(numSmallDogs || numLargeDogs || numCats || numOther);

  useEffect(() => {
    if (!hasPets) {
      setValue(FIELD_IDS.PETS_DESCRIPTION, null);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [hasPets]);

  const errorSmallDogs = get(errors, FIELD_IDS.SMALL_DOGS);
  const errorLargeDogs = get(errors, FIELD_IDS.LARGE_DOGS);
  const errorCats = get(errors, FIELD_IDS.CATS);
  const errorOtherPets = get(errors, FIELD_IDS.PETS_OTHER);

  const parseNumber = (value: string) => {
    const maxLen = 2;
    const number = parseInt(value.replace(/[^0-9]/g, '').substring(0, maxLen) || '0');
    return isNaN(number) ? 0 : number;
  };

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ fontFamily: 'heading-alt' }}>
        How many pets will be living in the home ?
      </Heading>
      <Text>Assistance animals do not need to be listed</Text>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: { base: 'none', lg: 'loose' } }}>
        <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' } }}>
          <Controller
            name={FIELD_IDS.LARGE_DOGS}
            rules={{ validate: numPetsValidation }}
            render={({ field: { onChange, value } }) => (
              <FormField
                css={{ marginTop: 'default' }}
                label={<Label>Large dogs (40+ pounds)</Label>}
                control={
                  <Select
                    value={value ? value : 0}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChange(parseNumber(e.target.value));
                    }}
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                  </Select>
                }
                description={
                  errorLargeDogs?.message ? <FormHelp>{String(errorLargeDogs.message)}</FormHelp> : undefined
                }
                error={!!errorLargeDogs}
              />
            )}
          />
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' } }}>
          <Controller
            name={FIELD_IDS.SMALL_DOGS}
            rules={{ validate: numPetsValidation }}
            render={({ field: { onChange, value } }) => (
              <FormField
                css={{ marginTop: 'default' }}
                label={<Label>Small dogs</Label>}
                control={
                  <Select
                    value={value ? value : 0}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChange(parseNumber(e.target.value));
                    }}
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                  </Select>
                }
                description={
                  errorSmallDogs?.message ? <FormHelp>{String(errorSmallDogs.message)}</FormHelp> : undefined
                }
                error={!!errorSmallDogs}
              />
            )}
          />
        </Box>
      </Box>

      <Controller
        name={FIELD_IDS.CATS}
        rules={{ validate: numPetsValidation }}
        render={({ field: { onChange, value } }) => (
          <FormField
            css={{ marginTop: 'default' }}
            label={<Label>Cats</Label>}
            control={
              <Select
                value={value ? value : 0}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  onChange(parseNumber(e.target.value));
                }}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
                <option value={9}>9</option>
              </Select>
            }
            description={errorCats?.message ? <FormHelp>{String(errorCats.message)}</FormHelp> : undefined}
            error={!!errorCats}
          />
        )}
      />

      <Controller
        name={FIELD_IDS.PETS_OTHER}
        rules={{ validate: numPetsValidation }}
        render={({ field: { onChange, value } }) => (
          <FormField
            css={{ marginTop: 'default' }}
            label={<Label>Other pets</Label>}
            control={
              <Select
                value={value ? value : 0}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  onChange(parseNumber(e.target.value));
                }}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
                <option value={9}>9</option>
              </Select>
            }
            description={errorOtherPets?.message ? <FormHelp>{String(errorOtherPets.message)}</FormHelp> : undefined}
            error={!!errorOtherPets}
          />
        )}
      />

      {hasPets && (
        <FormField
          css={{ marginTop: 'default' }}
          label={<Label>Details</Label>}
          control={
            <Textarea
              maxLength={TEXT_AREA_MAX_LENGTH_510}
              css={{ resize: 'vertical' }}
              {...register(FIELD_IDS.PETS_DESCRIPTION)}
            />
          }
          description={
            <FormHelp>
              Include details such as dog breed, vaccination history, or any pets that aren’t dogs or cats
            </FormHelp>
          }
          optional
        />
      )}
    </>
  );
}

export function PetsStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconPetsFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
            Assistance or support animals
          </Heading>
          <Paragraph>
            An assistance or support animal helps a person with a disability or eases the symptoms of a person’s
            disability.
          </Paragraph>
          <Paragraph css={{ marginTop: 'default' }}>
            Under federal law, assistance animals are not considered pets. In some cases, housing providers must allow
            assistance animals if there is a disability-related need.
          </Paragraph>
          <TextButton asChild css={{ marginTop: 'default' }}>
            <a href={ASSISTANCE_ANIMALS_URL} target="_blank" rel="noopener noreferrer">
              Learn more
            </a>
          </TextButton>
        </>
      }
    />
  );
}

const numPetsValidation = (value: number) => {
  if (!value || (Number.isInteger(value) && value >= 0)) {
    return true;
  } else if (!Number.isInteger(value)) {
    return 'Enter a number without any decimals or fractions';
  } else {
    return 'Minimum value is 0';
  }
};
